// TODO (Gabe 2025-02-19): There is probably a lot of logic in here
// that can probably be shared with the AssetPicker.
import type {
  ObjectPositionXType,
  ObjectPositionYType,
} from "@v1/types/object-fit";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import * as React from "react";

import AssetLibraryPopover from "@v1/components/assets/AssetLibraryPopover";
import ChevronMenuIndicator from "@v1/components/common/designSystem/ChevronMenuIndicator";
import Selectable from "@v1/components/designSystem/Selectable";
import { DynamicDataSelector } from "@v1/components/editor/page/DynamicDataSelector";
import ControlGroup from "@v1/components/editor/page/element-editor/components/extras/ControlGroup";
import ModifierLabel from "@v1/components/editor/page/element-editor/components/extras/ModifierLabel";
import { LengthInputSelector } from "@v1/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import DynamicData from "@v1/svg/dynamic-data";
import startCase from "lodash-es/startCase";
import { ArrowLeftRight, ArrowUpDown } from "lucide-react";
import useMeasure from "react-use-measure";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import {
  CSS_LENGTH_TYPES_WITH_COMPUTED,
  CSS_LENGTH_TYPES_WITH_PERCENTAGE,
} from "replo-runtime/shared/utils/units";

export type ObjectSizeType = "auto" | "contain" | "cover" | "custom" | string;

export type ObjectRepeatType = "no-repeat" | "repeat" | "repeat-x" | "repeat-y";

type BackgroundAssetPickerProps = {
  url?: string | ReploMixedStyleValue;
  backgroundPositionValue?: { x?: string; y?: string };
  onChangeBackgroundPositionX?(value: ObjectPositionXType): void;
  onChangeBackgroundPositionY?(value: ObjectPositionYType): void;
  backgroundSizeValue?: ObjectSizeType;
  onChangeBackgroundSize?(value: ObjectSizeType): void;
  backgroundRepeatValue?: ObjectRepeatType;
  onChangeBackgroundRepeat?(value: ObjectRepeatType): void;
  allowsSettingDynamicData?: boolean;
  onChangeSource?(url: string): void;
};

const getBackgroundSizeValues = (
  bgSizeValue?: string,
): { size: string; width: string; height: string } => {
  if (!bgSizeValue) {
    return { size: "auto", width: "auto", height: "auto" };
  }
  if (["auto", "cover", "contain"].includes(bgSizeValue)) {
    return { size: bgSizeValue, width: "auto", height: "auto" };
  }
  const [width, height] = bgSizeValue.split(" ");
  return { size: "custom", width: width ?? "100%", height: height ?? "100%" };
};

const BackgroundAssetPicker = ({
  url,
  backgroundRepeatValue,
  onChangeBackgroundRepeat,
  backgroundPositionValue,
  backgroundSizeValue,
  onChangeBackgroundSize,
  onChangeBackgroundPositionX,
  onChangeBackgroundPositionY,
  allowsSettingDynamicData = false,
  onChangeSource,
}: BackgroundAssetPickerProps) => {
  const [backgroundPositionXValue, setBackgroundPositionXValue] =
    useOverridableState<ObjectPositionXType | undefined>(
      backgroundPositionValue?.x,
    );
  const [backgroundPositionYValue, setBackgroundPositionYValue] =
    useOverridableState<ObjectPositionYType | undefined>(
      backgroundPositionValue?.y,
    );
  const [backgroundSize, setBackgroundSize] = useOverridableState<
    ObjectPositionYType | undefined
  >(getBackgroundSizeValues(backgroundSizeValue).size);
  const [backgroundSizeWidth, setBackgroundSizeWidth] =
    useOverridableState<string>(
      getBackgroundSizeValues(backgroundSizeValue).width,
    );
  const [backgroundSizeHeight, setBackgroundSizeHeight] =
    useOverridableState<string>(
      getBackgroundSizeValues(backgroundSizeValue).height,
    );
  const [selectableRef, { width }] = useMeasure({ offsetSize: true });
  const [isAssetPickerPopoverOpen, setIsAssetPickerPopoverOpen] =
    React.useState(false);

  const backgroundSizeOptions = [
    { value: "auto", label: "Default" },
    { value: "contain", label: "Contain" },
    { value: "cover", label: "Cover" },
    { value: "custom", label: "Custom" },
  ];

  const backgroundRepeatOptions = [
    { label: "Tile", value: "repeat" },
    { label: "No Repeat", value: "no-repeat" },
    { label: "Repeat X", value: "repeat-x" },
    { label: "Repeat Y", value: "repeat-y" },
  ];

  function _onSelectX(value: ObjectPositionXType) {
    setBackgroundPositionXValue(value);
    onChangeBackgroundPositionX?.(value);
  }

  function _onSelectY(value: ObjectPositionYType) {
    setBackgroundPositionYValue(value);
    onChangeBackgroundPositionY?.(value);
  }

  function _onChangeWidthSize(value: string) {
    setBackgroundSizeWidth(value);
    const bgSizeValue = `${value} ${backgroundSizeHeight ?? "100%"}`;
    onChangeBackgroundSize?.(bgSizeValue);
  }

  function _onChangeHeightSize(value: string) {
    setBackgroundSizeHeight(value);
    const bgSizeValue = `${backgroundSizeWidth ?? "100%"} ${value}`;
    onChangeBackgroundSize?.(bgSizeValue);
  }

  const backgroundSizeDimensionsOptions = [
    {
      id: "auto",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      value: "100%",
      title: "Reset",
    },
    {
      id: "50%",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      value: "50%",
      title: "50%",
    },
    {
      id: "100%",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      value: "100%",
      title: "100%",
    },
  ];

  const backgroundPositionXOptions = [
    {
      id: "left",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      value: "left",
      title: "Left",
      onSelect: () => _onSelectX("left"),
    },
    {
      id: "center",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      value: "center",
      title: "Center",
      onSelect: () => _onSelectX("center"),
    },
    {
      id: "right",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      value: "right",
      title: "Right",
      onSelect: () => _onSelectX("right"),
    },
  ];

  const backgroundPositionYOptions = [
    {
      id: "top",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      title: "Top",
      onSelect: () => _onSelectY("top"),
    },
    {
      id: "center",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      title: "Center",
      onSelect: () => _onSelectY("center"),
    },
    {
      id: "bottom",
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      title: "Bottom",
      onSelect: () => _onSelectY("bottom"),
    },
  ];

  const onSelectAsset = () => setIsAssetPickerPopoverOpen(true);
  const applyComponentAction = useApplyComponentAction();
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-[150px] cursor-pointer" onClick={onSelectAsset}>
        {url ? (
          <div
            className="h-full w-full rounded-sm object-cover object-center"
            style={{
              backgroundImage: `url(${url})`,
              backgroundSize:
                backgroundSize === "custom"
                  ? `${backgroundSizeWidth} ${backgroundSizeHeight}`
                  : backgroundSize,
              backgroundPositionX: backgroundPositionXValue,
              backgroundPositionY: backgroundPositionYValue,
              backgroundRepeat: backgroundRepeatValue,
            }}
          />
        ) : (
          <div className="bg-sheet flex h-full w-full items-center justify-center rounded-sm">
            <span className="text-muted text-xs">Select Background</span>
          </div>
        )}
      </div>
      <div className="flex h-6 gap-2">
        <AssetLibraryPopover
          isOpen={isAssetPickerPopoverOpen}
          setIsOpen={setIsAssetPickerPopoverOpen}
          onChangeSource={onChangeSource}
          assetTypes={["image"]}
          anchor={
            <div
              onClick={onSelectAsset}
              className="bg-sheet flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-sm"
            >
              <span className="text-default text-xs">
                {url ? "Change Background" : "Select Background"}
              </span>
            </div>
          }
        />
        {allowsSettingDynamicData && (
          <DynamicDataSelector
            targetType="image"
            triggerClassName="w-fit"
            trigger={
              <div className="bg-surface flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center gap-1 rounded-sm">
                <DynamicData />
              </div>
            }
            onChange={(value) => {
              applyComponentAction({
                type: "setStyles",
                value: {
                  backgroundImage: value,
                  backgroundSize: "cover",
                },
              });
            }}
          />
        )}
      </div>
      <div className="flex w-full flex-col gap-2">
        <ControlGroup label="Size">
          <div className="w-full" ref={selectableRef}>
            <Selectable
              placeholder="Choose Size"
              options={backgroundSizeOptions}
              value={backgroundSize}
              defaultValue="cover"
              onSelect={(value: ObjectSizeType) => {
                let bgSizeValue = value;
                setBackgroundSize(value);
                if (value === "custom") {
                  bgSizeValue = `${backgroundSizeWidth ?? "100%"} ${
                    backgroundSizeHeight ?? "100%"
                  }`;
                }
                return onChangeBackgroundSize?.(bgSizeValue);
              }}
              isDisabled={!url}
            />
          </div>
        </ControlGroup>
        <div className="w-full">
          <LengthInputSelector
            value={backgroundSizeWidth}
            onChange={_onChangeWidthSize}
            label={<ModifierLabel label="Width" />}
            startEnhancer={<ArrowLeftRight size={14} />}
            endEnhancer={() => (
              <ChevronMenuIndicator
                items={backgroundSizeDimensionsOptions.map((option) => ({
                  ...option,
                  onSelect: () => _onChangeWidthSize(option.value),
                }))}
                menuWidth={width}
              />
            )}
            field="BackgroundSizeWidth"
            allowsNegativeValue={false}
            placeholder="auto"
            metrics={CSS_LENGTH_TYPES_WITH_PERCENTAGE}
            isDisabled={!url || backgroundSize !== "custom"}
            autofocus={Boolean(url)}
            resetValue="auto"
            previewProperty="backgroundSize"
            previewSubProperty="backgroundSizeWidth"
            dragTrigger="label"
          />
        </div>
        <LengthInputSelector
          value={backgroundSizeHeight}
          onChange={_onChangeHeightSize}
          startEnhancer={<ArrowUpDown size={14} />}
          endEnhancer={() => (
            <ChevronMenuIndicator
              items={backgroundSizeDimensionsOptions.map((option) => ({
                ...option,
                onSelect: () => _onChangeHeightSize(option.value),
              }))}
              menuWidth={width}
            />
          )}
          allowsNegativeValue={false}
          field="BackgroundSizeHeight"
          placeholder="auto"
          metrics={CSS_LENGTH_TYPES_WITH_PERCENTAGE}
          isDisabled={!url || backgroundSize !== "custom"}
          resetValue="auto"
          previewProperty="backgroundSize"
          previewSubProperty="backgroundSizeHeight"
          label={<ModifierLabel label="Height" />}
          dragTrigger="label"
        />
        <ControlGroup label="Repeat">
          <Selectable
            placeholder="-"
            layoutClassName="w-full"
            triggerLayoutClassName="w-full"
            options={backgroundRepeatOptions}
            value={backgroundRepeatValue}
            defaultValue="repeat"
            onSelect={(value: ObjectRepeatType) => {
              return onChangeBackgroundRepeat?.(value);
            }}
            isDisabled={!url}
          />
        </ControlGroup>
        <LengthInputSelector
          value={
            backgroundPositionXValue?.includes("px") ||
            backgroundPositionXValue?.includes("%")
              ? backgroundPositionXValue
              : startCase(backgroundPositionXValue)
          }
          onChange={_onSelectX}
          endEnhancer={() => (
            <ChevronMenuIndicator
              items={backgroundPositionXOptions}
              menuWidth={width}
            />
          )}
          field="PositionX"
          placeholder="50%"
          metrics={CSS_LENGTH_TYPES_WITH_COMPUTED}
          isDisabled={!url}
          autofocus={Boolean(url)}
          allowedNonUnitValues={["left", "center", "right"]}
          previewProperty="backgroundPositionX"
          resetValue="center"
          dragTrigger="label"
          label={<ModifierLabel label="X Position" />}
        />
        <LengthInputSelector
          value={
            backgroundPositionYValue?.includes("px") ||
            backgroundPositionYValue?.includes("%")
              ? backgroundPositionYValue
              : startCase(backgroundPositionYValue)
          }
          onChange={_onSelectY}
          endEnhancer={() => (
            <ChevronMenuIndicator
              items={backgroundPositionYOptions}
              menuWidth={width}
            />
          )}
          field="PositionY"
          placeholder="50%"
          metrics={CSS_LENGTH_TYPES_WITH_COMPUTED}
          isDisabled={!url}
          allowedNonUnitValues={["left", "center", "right"]}
          previewProperty="backgroundPositionY"
          resetValue="center"
          dragTrigger="label"
          label={<ModifierLabel label="Y Position" />}
        />
      </div>
    </div>
  );
};

export default BackgroundAssetPicker;
